import type { CloudBackup, TransferTicket } from './api-client';

type SnapshotBytes = ArrayBuffer | Uint8Array;

export class SnapshotTransferError extends Error {
  constructor(
    public readonly code:
      | 'download-failed'
      | 'integrity-failed'
      | 'invalid-ticket'
      | 'upload-failed',
    message: string,
  ) {
    super(message);
    this.name = 'SnapshotTransferError';
  }
}

export async function describeSnapshot(
  bytes: SnapshotBytes,
  cryptoProvider: Crypto = crypto,
): Promise<Readonly<{ checksum_sha256: string; size_bytes: number }>> {
  const snapshot = toUint8Array(bytes);
  const digest = await cryptoProvider.subtle.digest('SHA-256', snapshot);
  return {
    checksum_sha256: bytesToBase64(new Uint8Array(digest)),
    size_bytes: snapshot.byteLength,
  };
}

export async function uploadSnapshot(
  ticket: TransferTicket,
  bytes: SnapshotBytes,
  fetcher: typeof fetch = fetch,
): Promise<void> {
  validateTicket(ticket, 'PUT');
  let response: Response;
  try {
    response = await fetcher(ticket.url, {
      body: toUint8Array(bytes),
      headers: ticket.headers,
      method: ticket.method,
    });
  } catch (error) {
    throw new SnapshotTransferError(
      'upload-failed',
      error instanceof Error ? error.message : 'Snapshot upload failed.',
    );
  }
  if (!response.ok) {
    throw new SnapshotTransferError(
      'upload-failed',
      `Snapshot upload failed with HTTP ${response.status}.`,
    );
  }
}

export async function downloadSnapshot(
  ticket: TransferTicket,
  backup: CloudBackup,
  fetcher: typeof fetch = fetch,
  cryptoProvider: Crypto = crypto,
): Promise<ArrayBuffer> {
  validateTicket(ticket, 'GET');
  let response: Response;
  try {
    response = await fetcher(ticket.url, {
      headers: ticket.headers,
      method: ticket.method,
    });
  } catch (error) {
    throw new SnapshotTransferError(
      'download-failed',
      error instanceof Error ? error.message : 'Snapshot download failed.',
    );
  }
  if (!response.ok) {
    throw new SnapshotTransferError(
      'download-failed',
      `Snapshot download failed with HTTP ${response.status}.`,
    );
  }

  const bytes = await response.arrayBuffer();
  const description = await describeSnapshot(bytes, cryptoProvider);
  if (
    description.size_bytes !== backup.size_bytes ||
    description.checksum_sha256 !== backup.checksum_sha256
  ) {
    throw new SnapshotTransferError(
      'integrity-failed',
      'Downloaded snapshot failed integrity validation.',
    );
  }
  return bytes;
}

function validateTicket(ticket: TransferTicket, method: 'GET' | 'PUT'): void {
  let url: URL;
  try {
    url = new URL(ticket.url);
  } catch {
    throw new SnapshotTransferError('invalid-ticket', 'Invalid transfer URL.');
  }
  const isSecure =
    url.protocol === 'https:' ||
    (url.protocol === 'http:' &&
      (url.hostname === 'localhost' || url.hostname === '127.0.0.1'));
  if (!isSecure || ticket.method.toUpperCase() !== method) {
    throw new SnapshotTransferError(
      'invalid-ticket',
      'Invalid snapshot transfer ticket.',
    );
  }
}

function toUint8Array(bytes: SnapshotBytes): Uint8Array<ArrayBuffer> {
  if (bytes instanceof ArrayBuffer) return new Uint8Array(bytes);
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}
